import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import type { ContactAttributeDefinition, CreateAttributeDefinitionPayload, UpdateAttributeDefinitionPayload } from "@/types/contacts";
import { contactAttributeDefinitionService } from "@/lib/api-services/contact-attribute-definition-service";

interface ContactAttributeDefinitionState {
  definitions: ContactAttributeDefinition[];
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;

  fetchDefinitions: (accountId: string) => Promise<void>;
  createDefinition: (accountId: string, data: CreateAttributeDefinitionPayload) => Promise<ContactAttributeDefinition>;
  updateDefinition: (accountId: string, id: string, data: UpdateAttributeDefinitionPayload) => Promise<ContactAttributeDefinition>;
  deleteDefinition: (accountId: string, id: string) => Promise<void>;
  reset: () => void;
}

export const useContactAttributeDefinitionStore = create<ContactAttributeDefinitionState>()(
  immer((set) => ({
    definitions: [],
    isLoading: false,
    isSaving: false,
    error: null,

    fetchDefinitions: async (accountId) => {
      set((state) => { state.isLoading = true; state.error = null; });
      try {
        const definitions = await contactAttributeDefinitionService.getDefinitions(accountId);
        set((state) => {
          state.definitions = definitions;
          state.isLoading = false;
        });
      } catch (err) {
        set((state) => {
          state.isLoading = false;
          state.error = err instanceof Error ? err.message : "Failed to load attribute definitions";
        });
      }
    },

    createDefinition: async (accountId, data) => {
      set((state) => { state.isSaving = true; state.error = null; });
      try {
        const definition = await contactAttributeDefinitionService.createDefinition(accountId, data);
        set((state) => {
          state.definitions.push(definition);
          state.definitions.sort((a, b) => a.displayOrder - b.displayOrder);
          state.isSaving = false;
        });
        return definition;
      } catch (err) {
        set((state) => {
          state.isSaving = false;
          state.error = err instanceof Error ? err.message : "Failed to create attribute definition";
        });
        throw err;
      }
    },

    updateDefinition: async (accountId, id, data) => {
      set((state) => { state.isSaving = true; state.error = null; });
      try {
        const updated = await contactAttributeDefinitionService.updateDefinition(accountId, id, data);
        set((state) => {
          const idx = state.definitions.findIndex((d) => d.id === id);
          if (idx !== -1) state.definitions[idx] = updated;
          state.definitions.sort((a, b) => a.displayOrder - b.displayOrder);
          state.isSaving = false;
        });
        return updated;
      } catch (err) {
        set((state) => {
          state.isSaving = false;
          state.error = err instanceof Error ? err.message : "Failed to update attribute definition";
        });
        throw err;
      }
    },

    deleteDefinition: async (accountId, id) => {
      set((state) => { state.isSaving = true; state.error = null; });
      try {
        await contactAttributeDefinitionService.deleteDefinition(accountId, id);
        set((state) => {
          state.definitions = state.definitions.filter((d) => d.id !== id);
          state.isSaving = false;
        });
      } catch (err) {
        set((state) => {
          state.isSaving = false;
          state.error = err instanceof Error ? err.message : "Failed to delete attribute definition";
        });
        throw err;
      }
    },

    reset: () => set((state) => {
      state.definitions = [];
      state.isLoading = false;
      state.isSaving = false;
      state.error = null;
    }),
  }))
);
